import React, { useEffect, useRef, useState } from "react";
import { FiGrid, FiList, FiSliders, FiX } from "react-icons/fi";
import { LuArrowDownUp } from "react-icons/lu";

const MOBILE_SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Date, new to old" },
  { value: "oldest", label: "Date, old to new" },
  { value: "nameAsc", label: "Alphabetically, A-Z" },
  { value: "nameDesc", label: "Alphabetically, Z-A" },
  { value: "priceLow", label: "Price, low to high" },
  { value: "priceHigh", label: "Price, high to low" },
];

export const isMobileSortValue = (value) =>
  MOBILE_SORT_OPTIONS.some((option) => option.value === value);

export function useMobileCollectionDrawer() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const triggerRef = useRef(null);
  const drawerRef = useRef(null);
  const sortTriggerRef = useRef(null);
  const sortDialogRef = useRef(null);

  useEffect(() => {
    if (!filtersOpen && !sortOpen) return undefined;
    const trigger = filtersOpen ? triggerRef.current : sortTriggerRef.current;
    const activeDialog = filtersOpen ? drawerRef.current : sortDialogRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    activeDialog?.querySelector(".cp-mobile-close")?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setFiltersOpen(false);
        setSortOpen(false);
      }
      if (event.key !== "Tab" || !activeDialog) return;
      const elements = [...activeDialog.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')];
      if (!elements.length) return;
      if (event.shiftKey && document.activeElement === elements[0]) {
        event.preventDefault();
        elements[elements.length - 1].focus();
      } else if (!event.shiftKey && document.activeElement === elements[elements.length - 1]) {
        event.preventDefault();
        elements[0].focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      trigger?.focus();
    };
  }, [filtersOpen, sortOpen]);

  return { filtersOpen, setFiltersOpen, sortOpen, setSortOpen, viewMode, setViewMode, triggerRef, drawerRef, sortTriggerRef, sortDialogRef };
}

export default function MobileCollectionControls({ title, count, sortBy, onSort, drawer, supportsDateSort = true, supportsFeaturedSort = true }) {
  const sortOptions = MOBILE_SORT_OPTIONS
    .filter((option) => (supportsFeaturedSort || option.value !== "featured") && (supportsDateSort || option.value !== "oldest"))
    .map((option) => option.value === "newest" && !supportsDateSort ? { ...option, label: "Default order" } : option);
  const selectedSort = sortOptions.find((option) => option.value === sortBy);
  return (
    <div className="cp-mobile-heading">
      <h1>{title}</h1>
      <p>{count} products</p>
      <div className="cp-mobile-toolbar">
        <button ref={drawer.triggerRef} type="button" onClick={() => drawer.setFiltersOpen(true)}><FiSliders /> Filters</button>
        <button ref={drawer.sortTriggerRef} type="button" className="cp-mobile-sort" aria-haspopup="dialog" aria-expanded={drawer.sortOpen} onClick={() => drawer.setSortOpen(true)}><LuArrowDownUp /> Sort by</button>
        <div className="cp-mobile-view" role="group" aria-label="Product layout">
          <button type="button" aria-label="Grid view" aria-pressed={drawer.viewMode === "grid"} onClick={() => drawer.setViewMode("grid")}><FiGrid /></button>
          <button type="button" aria-label="List view" aria-pressed={drawer.viewMode === "list"} onClick={() => drawer.setViewMode("list")}><FiList /></button>
        </div>
      </div>
      {drawer.sortOpen && (
        <div className="cp-mobile-sort-layer">
          <button type="button" className="cp-mobile-backdrop" aria-label="Close sorting" onClick={() => drawer.setSortOpen(false)} />
          <section ref={drawer.sortDialogRef} className="cp-mobile-sort-sheet" role="dialog" aria-modal="true" aria-label="Sort products">
            <div className="cp-mobile-sort-header">
            <div><h2>Sort by</h2><p>Selected: {selectedSort?.label || "Default order"}</p></div>
              <button type="button" className="cp-mobile-close" aria-label="Close sorting" onClick={() => drawer.setSortOpen(false)}><FiX size={21} /></button>
            </div>
            <div className="cp-mobile-sort-options">
              {sortOptions.map((option) => (
                <button key={option.value} type="button" className={sortBy === option.value ? "is-selected" : ""} aria-pressed={sortBy === option.value} onClick={() => { onSort(option.value); drawer.setSortOpen(false); }}>
                  {option.label}
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export function MobileFilterActions({ onClose }) {
  return (
    <div className="cp-mobile-filter-actions">
      <button type="button" className="cp-mobile-apply" onClick={onClose}>Show products</button>
    </div>
  );
}

export function MobileFilterClose({ onClose }) {
  return <button type="button" className="cp-mobile-close" onClick={onClose} aria-label="Close filters"><FiX size={21} /></button>;
}
